import type { Column, Table } from '@tanstack/react-table';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { DataTableHeaderCell } from '../../_base/index.js';
import { DataTableHeaderCellList } from '../../_base/list/index.js';
import { DataTableHeaderCellSurface } from '../../_base/surface/index.js';
import { useListFilter } from '../../_hooks/list/_shared/hooks/list-filter.js';
import { useStringFilterData } from '../../_hooks/list/string.js';
import { useOpenPopover } from '../../_hooks/open-popover.js';
import { ResetFilterMenuItem } from '../../_shared/reset.js';
import { typedSetStringColumnFilterValue } from '../filter/index.js';
import { STRING_FILTER_OPERATION, type StringFilterValuesWithoutInArray } from '../filter/operation.js';
import type { StringFilterSchemaWithoutInArray } from '../filter/schema.js';
import { DROPDOWN_STRING_OPTIONS } from './data.js';
import { StringFilter } from './filter.js';
import { StringOperation } from './operation.js';

type DataTableHeaderCellStringProps<TData, TValue> = {
    tableId: string;
    table: Table<TData>;
    column: Column<TData, TValue>;
    title: ReactNode;
    tooltip: ReactNode;
};

export function DataTableHeaderCellString<TData, TValue>({
    tableId,
    table,
    column,
    title,
    tooltip,
}: DataTableHeaderCellStringProps<TData, TValue>) {
    const filter = column.getFilterValue() as StringFilterSchemaWithoutInArray | undefined;

    const [dropdownValue, setDropdownValue] = useState<StringFilterValuesWithoutInArray['id']>(
        filter?.operation ?? DROPDOWN_STRING_OPTIONS[0][0]
    );

    const { open, openPopover, closePopover, onOpenChange } = useOpenPopover();

    const disabled = useMemo(() => table.options.data.length > 10_000, [table.options.data]);
    const data = useStringFilterData({ disabled, table, column });

    const onResetDropdownValue = useCallback(() => {
        setDropdownValue(DROPDOWN_STRING_OPTIONS[0][0]);
    }, []);

    const onRegularSelectionChange = useCallback((selectedValues: (string | number)[], items: string[]) => {
        setDropdownValue(STRING_FILTER_OPERATION.inArray.id as StringFilterValuesWithoutInArray['id']);

        typedSetStringColumnFilterValue(column, {
            operation: STRING_FILTER_OPERATION.inArray.id,
            array: selectedValues.filter((value) => typeof value === 'string'),
            includeBlanks: items.includes('select-blanks'),
        });
    }, []);

    const { selectedItems, handleSelectionChange, onApplyFilter, resetListFilter } = useListFilter({
        data,
        disabled: disabled,
        tableId,
        column,
        onResetDropdownValue,
        onRegularSelectionChange,
    });

    return (
        <DataTableHeaderCell
            column={column}
            title={title}
            open={open}
            openPopover={openPopover}
            closePopover={closePopover}
            onOpenChange={onOpenChange}
            tooltip={tooltip}
            popover={
                <DataTableHeaderCellSurface
                    column={column}
                    closePopover={closePopover}
                    filterNode={
                        <>
                            <ResetFilterMenuItem
                                column={column}
                                closePopover={closePopover}
                                resetOperation={resetListFilter}
                            />

                            <StringOperation
                                column={column}
                                dropdownValue={dropdownValue}
                                resetListFilter={resetListFilter}
                                setDropdownValue={setDropdownValue}
                            />

                            {dropdownValue ===
                            (STRING_FILTER_OPERATION.inArray.id as StringFilterValuesWithoutInArray['id']) ? null : (
                                <StringFilter column={column} dropdownValue={dropdownValue} />
                            )}

                            {!disabled && (
                                <DataTableHeaderCellList
                                    data={data}
                                    selectedItems={selectedItems}
                                    handleSelectionChange={handleSelectionChange}
                                    onApplyFilter={onApplyFilter}
                                />
                            )}
                        </>
                    }
                />
            }
        />
    );
}
